import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactCountryFlag from "react-country-flag";
import { ChevronDown, ChevronUp, Search } from "lucide-react";
import "./LanguageSwitcher.css";
import Cookies from "js-cookie";
import {
  getCustomerProfile,
  updatePreferredLanguage,
} from "../../services/auth.service";

const languages = [
  {
    code: "en",
    countryCode: "GB",
    names: {
      en: "English",
      de: "Englisch",
      es: "Inglés",
      fr: "Anglais",
    },
  },
  {
    code: "de",
    countryCode: "DE",
    names: {
      en: "German",
      de: "Deutsch",
      es: "Alemán",
      fr: "Allemand",
    },
  },
  {
    code: "es",
    countryCode: "ES",
    names: {
      en: "Spanish",
      de: "Spanisch",
      es: "Español",
      fr: "Espagnol",
    },
  },
  {
    code: "fr",
    countryCode: "FR",
    names: {
      en: "French",
      de: "Französisch",
      es: "Francés",
      fr: "Français",
    },
  },
];

// UI text per language (for the switcher itself)
const switcherUI = {
  en: {
    searchPlaceholder: "Search language...",
    noResult: "No language found",
  },
  de: {
    searchPlaceholder: "Sprache suchen...",
    noResult: "Keine Sprache gefunden",
  },
  es: {
    searchPlaceholder: "Buscar idioma...",
    noResult: "No se encontró ningún idioma",
  },
  fr: {
    searchPlaceholder: "Rechercher une langue...",
    noResult: "Aucune langue trouvée",
  },
};

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const dropdownRef = useRef(null);

  /*
   * Get current language code.
   * "de" -> "de", "de-DE" -> "de"
   */
  const currentLangCode = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "en"
  ).split("-")[0];

  // Find current language object
  const currentLanguage =
    languages.find(
      (language) => language.code === currentLangCode
    ) || languages[0];

  // Get UI text for current language
  const ui = switcherUI[currentLangCode] || switcherUI.en;

  /*
   * Helper: get translated name of a language
   * based on the current UI language.
   */
  const getLanguageName = (language) => {
    return (
      language.names[currentLangCode] ||
      language.names.en ||
      language.code
    );
  };

  /*
   * Filter languages according to search.
   * Search matches translated names too.
   */
  const filteredLanguages = languages.filter((language) =>
    getLanguageName(language)
      .toLowerCase()
      .includes(searchTerm.toLowerCase().trim())
  );

  /*
   * Change language
   */
  const changeLanguage = async (languageCode) => {
  if (languageCode === currentLangCode) {
    setIsOpen(false);
    setSearchTerm("");
    return;
  }

  try {
    // Save preference for guest and logged-in customers
    localStorage.setItem("selectedLanguage", languageCode);

    // Update the website immediately
    await i18n.changeLanguage(languageCode);
    document.documentElement.lang = languageCode;

    setIsOpen(false);
    setSearchTerm("");

    // Save preference to the database for logged-in customers
    if (Cookies.get("token")) {
      try {
        await updatePreferredLanguage(languageCode);
        console.log("Customer language preference saved");
      } catch (error) {
        console.error(
          "Failed to save language preference to customer account:",
          error
        );
      }
    }
  } catch (error) {
    console.error("Language change failed:", error);
  }
};

  /*
   * Load saved language on mount
   */
  /*
 * Load saved language from customer account
 */
useEffect(() => {
  let cancelled = false;

  const loadCustomerLanguage = async () => {
    if (!Cookies.get("token")) {
      // Guest language is restored by the i18next detector.
      return;
    }

    try {
      const response = await getCustomerProfile();

      if (cancelled) return;

      const preferredLanguage =
        response?.data?.preferredLanguage;

      const isSupported = (language) =>
        languages.some((item) => item.code === language);

      if (
        preferredLanguage &&
        isSupported(preferredLanguage)
      ) {
        localStorage.setItem(
          "selectedLanguage",
          preferredLanguage
        );

        await i18n.changeLanguage(preferredLanguage);
        document.documentElement.lang = preferredLanguage;
      } else {
        // No account preference yet: keep the browser's choice
        const localLanguage =
          localStorage.getItem("selectedLanguage");

        if (localLanguage && isSupported(localLanguage)) {
          await updatePreferredLanguage(localLanguage);
        }
      }
    } catch (error) {
      console.error(
        "Failed to load customer language preference:",
        error
      );
    }
  };

  void loadCustomerLanguage();

  return () => {
    cancelled = true;
  };
}, [i18n]);
  useEffect(() => {
  const updateHtmlLanguage = (language) => {
    document.documentElement.lang = (language || "en").split("-")[0];
  };

  updateHtmlLanguage(i18n.resolvedLanguage || i18n.language);

  i18n.on("languageChanged", updateHtmlLanguage);

  return () => {
    i18n.off("languageChanged", updateHtmlLanguage);
  };
}, [i18n]);

  /*
   * Close dropdown when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /*
   * Close dropdown with Escape key
   */
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  return (
    <div className="language-switcher" ref={dropdownRef}>
      {/* =====================================
          SELECTED LANGUAGE BUTTON
          ===================================== */}
      <button
        type="button"
        className={`language-selector ${
          isOpen ? "language-selector-open" : ""
        }`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <ReactCountryFlag
          countryCode={currentLanguage.countryCode}
          svg
          className="selected-language-flag"
          aria-label={getLanguageName(currentLanguage)}
        />

        <span className="selected-language-name">
          {getLanguageName(currentLanguage)}
        </span>

        <span className="language-arrow">
          {isOpen ? (
            <ChevronUp size={17} strokeWidth={1.8} />
          ) : (
            <ChevronDown size={17} strokeWidth={1.8} />
          )}
        </span>
      </button>

      {/* =====================================
          DROPDOWN
          ===================================== */}
      {isOpen && (
        <div className="language-dropdown">
          {/* Search Box */}
          <div className="language-search">
            <Search
              size={17}
              strokeWidth={1.7}
              className="language-search-icon"
            />

            <input
              type="text"
              value={searchTerm}
              placeholder={ui.searchPlaceholder}
              onChange={(event) =>
                setSearchTerm(event.target.value)
              }
              autoFocus
              aria-label={ui.searchPlaceholder}
            />
          </div>

          {/* Language List */}
          <div className="language-list" role="listbox">
            {filteredLanguages.length > 0 ? (
              filteredLanguages.map((language) => {
                const isSelected =
                  currentLangCode === language.code;

                return (
                  <button
                    key={language.code}
                    type="button"
                    className={`language-option ${
                      isSelected
                        ? "language-option-selected"
                        : ""
                    }`}
                    onClick={() => changeLanguage(language.code)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <ReactCountryFlag
                      countryCode={language.countryCode}
                      svg
                      className="language-option-flag"
                      aria-label={getLanguageName(language)}
                    />

                    <span className="language-option-name">
                      {getLanguageName(language)}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="language-no-result">
                {ui.noResult}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;